import type { AttachmentBlockModel } from '@blocksuite/affine/model';
import { downloadBlob } from '@streamlain/workspace-editor/shared/resource';
import { filesize } from 'filesize';

import { resolveWorkspaceBlobFromCacheOrRemote } from '../../modules/workspace/utils/blob-cache';
import {
  resolveBlobDisplayName,
  resolveBlobDownloadName,
  splitBlobNameForRename,
} from '../../utils/blob-display-name';
import type {
  AttachmentViewerBaseProps,
  AttachmentViewerSource,
} from './types';

export function isEditableTextAttachment({
  mimeType,
  name,
}: Pick<AttachmentViewerSource, 'mimeType' | 'name'>): boolean {
  const normalizedMime = mimeType.toLowerCase();
  const normalizedName = name.toLowerCase();
  if (
    normalizedMime === 'text/html' ||
    normalizedMime === 'application/xhtml+xml' ||
    normalizedName.endsWith('.html') ||
    normalizedName.endsWith('.htm')
  ) {
    return false;
  }
  return (
    normalizedMime.startsWith('text/') ||
    normalizedMime === 'application/json' ||
    normalizedMime === 'application/xml' ||
    normalizedName.endsWith('.txt') ||
    normalizedName.endsWith('.md') ||
    normalizedName.endsWith('.markdown') ||
    normalizedName.endsWith('.json') ||
    normalizedName.endsWith('.xml')
  );
}

export function buildAttachmentSource(
  model: AttachmentBlockModel
): AttachmentViewerSource {
  const canRename = !model.store.readonly;
  return {
    kind: 'block',
    id: model.id,
    docId: model.store.id,
    blobId: model.props.sourceId ?? null,
    name: resolveBlobDisplayName({
      blobId: model.props.sourceId,
      occurrenceName: model.props.name,
      mimeType: model.props.type,
    }),
    mimeType: model.props.type,
    size: model.props.size,
    model,
    canRename,
    rename: canRename
      ? nextName => {
          model.store.updateBlock(model, { name: nextName });
        }
      : undefined,
    canReplaceBlob: canRename,
    replaceBlob: canRename
      ? replacement => {
          model.store.updateBlock(model, {
            sourceId: replacement.blobId,
            type: replacement.mimeType,
            size: replacement.size,
          });
        }
      : undefined,
    supportsBlockAnnotations: true,
    supportsPdfSearchIndex: true,
  };
}

export async function getAttachmentBlob(source: AttachmentViewerSource) {
  const sourceId = source.blobId;
  if (!sourceId) {
    return null;
  }

  let blob =
    source.model !== undefined
      ? await source.model.store.blobSync.get(sourceId)
      : null;

  if (!blob && source.workspaceId) {
    if (!source.blobContext)
      throw new Error('Initialized attachment blob scope required');
    blob = await resolveWorkspaceBlobFromCacheOrRemote({
      workspaceId: source.workspaceId,
      context: source.blobContext,
      key: sourceId,
    });
  }

  if (blob) {
    blob = new Blob([blob], { type: source.mimeType });
  }

  return blob;
}

export async function download(source: AttachmentViewerSource) {
  const blob = await getAttachmentBlob(source);
  if (!blob) return;

  await downloadBlob(
    blob,
    resolveBlobDownloadName({
      blobId: source.blobId,
      occurrenceName: source.name,
      mimeType: source.mimeType,
    })
  );
}

export function buildAttachmentProps(
  sourceOrModel: AttachmentBlockModel | AttachmentViewerSource
): AttachmentViewerBaseProps {
  const source =
    'props' in sourceOrModel
      ? buildAttachmentSource(sourceOrModel)
      : sourceOrModel;
  const { editableName, extension } = splitBlobNameForRename(source.name);
  const ext = extension.replace(/^\./u, '');
  const name = editableName;
  const size = source.size === null ? '—' : filesize(source.size);
  return { source, model: source.model, name, ext, size };
}

export { getAttachmentType } from '@affine/core/modules/media/utils';
